/**
 * FormField - Base form input component with label and error handling
 *
 * Features:
 * - Configurable label, placeholder, and input type
 * - Error state styling with message display
 * - Optional right adornment (e.g., for validation checkmarks)
 * - Disabled state support
 */

import React, { forwardRef } from 'react';

interface FormFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Field label */
  label: string;
  /** Error message to display */
  error?: string;
  /** Optional element to render on the right side of the input */
  rightAdornment?: React.ReactNode;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField({
  label,
  error,
  rightAdornment,
  className = '',
  disabled,
  ...inputProps
}, ref) {
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-2">
      <label className="text-base font-semibold text-catch-dark-blue-90">
        {label}
      </label>
      <div className="relative">
        <input
          {...inputProps}
          ref={ref}
          disabled={disabled}
          className={`h-14 w-full rounded-2xl border-1 border-solid bg-white text-base text-catch-dark-blue-90 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-catch-light-blue disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 ${
            hasError ? 'border-red-400' : 'border-gray-200'
          } ${className}`}
          style={{ padding: rightAdornment ? '0 40px 0 16px' : '0 16px' }}
        />
        {rightAdornment && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {rightAdornment}
          </div>
        )}
      </div>
      {hasError && <p className="text-sm text-red-500">{error}</p>}
    </div>
  );
});

export default FormField;
